import axios from 'axios';

// Fallback API URL for production if environment variable is not set
const defaultApiUrl = process.env.NODE_ENV === 'production' 
  ? 'https://api.saillab.co.jp' 
  : 'http://localhost:5000';

const API_URL = `${process.env.REACT_APP_API_URL || defaultApiUrl}/api`;

export interface BookingActionContext {
  id: string;
  title?: string; // Optional since backend might not provide it directly
  description?: string;
  startTime: Date;
  endTime: Date;
  timeZone: string;
  guestName: string;
  guestEmail: string;
  notes?: string;
  host: {
    id: string;
    name: string;
    email: string;
  };
  meetingType: {
    id: string;
    title: string;
    description?: string;
    duration: number[];
    slug: string;
    type?: 'individual' | 'collective' | 'round_robin' | 'event';
  };
  status: 'confirmed' | 'canceled' | 'rescheduled' | 'pending';
  bookingLanguage?: string; // Language selected during booking (e.g., 'en', 'ja')
  formSubmissionData?: Record<string, any>; // Original form data keyed by field IDs for pre-populating reschedule form
}

export interface TimeSlot {
  start: Date;
  end: Date;
  timeZone: string;
}

export interface RescheduleData {
  startTime: Date;
  endTime: Date;
  timeZone: string;
}

export interface CancelData {
  cancelReason?: string;
}

/**
 * Refund outcome returned by the cancellation API (Phase 5 — host payments).
 * Shape mirrors PaymentService.refundBooking + checkAutoRefundEligibility.
 */
export type CancellationRefundOutcome = {
  status: 'refunded' | 'partially_refunded' | 'not_eligible' | 'failed' | 'skipped';
  amountRefunded?: number;
  reason?:
    | 'non_refundable_policy'
    | 'requires_host_approval'
    | 'outside_refund_window'
    | 'already_refunded'
    | string;
};

export interface ValidationResult {
  isValid: boolean;
  message?: string;
  canReschedule?: boolean;
  canCancel?: boolean;
}

export class BookingActionService {
  /**
   * Decode token and get booking details for reschedule/cancel actions
   */
  static async getBookingByToken(token: string): Promise<BookingActionContext> {
    try {
      const response = await axios.get(`${API_URL}/bookings/reschedule/${token}`);
      
      if (!response.data || !response.data.booking) {
        throw new Error('Booking not found');
      }

      const { booking } = response.data;
      
      return {
        id: booking.id,
        title: booking.meetingType?.title || 'Meeting', // Use meeting type title as fallback
        description: booking.meetingType?.description,
        startTime: new Date(booking.startTime),
        endTime: new Date(booking.endTime),
        timeZone: booking.timeZone,
        guestName: booking.guestName,
        guestEmail: booking.guestEmail,
        notes: booking.notes,
        host: booking.host,
        meetingType: {
          id: booking.meetingType?.id || '',
          title: booking.meetingType?.title || 'Meeting',
          description: booking.meetingType?.description,
          duration: booking.meetingType?.duration || [30],
          slug: booking.meetingType?.slug || '',
          type: booking.meetingType?.type || 'individual',
        },
        status: booking.status,
        bookingLanguage: booking.bookingLanguage || 'en', // Language used during booking
        formSubmissionData: booking.formSubmissionData || {}, // Original form data for reschedule pre-population
      };
    } catch (error: any) {
      if (error.response?.status === 400) {
        throw new Error('Invalid token. This link may have expired.');
      } else if (error.response?.status === 404) {
        throw new Error('Booking not found. This meeting may have been canceled.');
      }
      throw new Error('Failed to load booking details. Please try again.');
    }
  }

  /**
   * Get available reschedule slots for a booking
   * Reuses existing slot generation logic from the booking system
   */
  static async getAvailableRescheduleSlots(
    context: BookingActionContext,
    date: string,
    timeZone: string
  ): Promise<TimeSlot[]> {
    try {
      const response = await axios.get(`${API_URL}/bookings/available-slots`, {
        params: {
          meetingTypeId: context.meetingType.id,
          date,
          timeZone,
        },
      });

      return response.data.availableTimeSlots?.map((slot: any) => ({
        start: new Date(slot.start),
        end: new Date(slot.end),
        timeZone: slot.timeZone,
      })) || [];
    } catch (error: any) {
      throw new Error('Failed to load available times. Please try again.');
    }
  }

  /**
   * Submit a reschedule request
   */
  static async submitReschedule(token: string, rescheduleData: RescheduleData): Promise<void> {
    try {
      await axios.post(`${API_URL}/bookings/reschedule/${token}`, rescheduleData);
    } catch (error: any) {
      if (error.response?.status === 400) {
        throw new Error(error.response.data?.message || 'Invalid reschedule request.');
      } else if (error.response?.status === 409) {
        throw new Error('The selected time slot is no longer available. Please choose another time.');
      }
      throw new Error('Failed to reschedule meeting. Please try again.');
    }
  }

  /**
   * Submit a cancellation request. Returns the refund outcome (Phase 5):
   *   - refunded / partially_refunded — amount was returned to the invitee
   *   - not_eligible — policy didn't permit auto-refund (reason explains why)
   *   - failed       — provider call errored, contact host
   *   - null         — booking had no captured payment (free meeting)
   */
  static async submitCancellation(
    token: string,
    cancelData: CancelData = {}
  ): Promise<{ refund: CancellationRefundOutcome | null }> {
    try {
      const res = await axios.post(`${API_URL}/bookings/cancel/${token}`, cancelData);
      return { refund: res.data?.refund || null };
    } catch (error: any) {
      if (error.response?.status === 400) {
        throw new Error(error.response.data?.message || 'Invalid cancellation request.');
      }
      throw new Error('Failed to cancel meeting. Please try again.');
    }
  }

  /**
   * Validate if a booking action is allowed
   */
  static validateBookingAction(
    context: BookingActionContext, 
    action: 'reschedule' | 'cancel'
  ): ValidationResult {
    // Check if booking is already canceled
    if (context.status === 'canceled') {
      return {
        isValid: false,
        message: 'This meeting has already been canceled.',
        canReschedule: false,
        canCancel: false,
      };
    }

    // Check if meeting is in the past
    const now = new Date();
    if (context.startTime < now) {
      return {
        isValid: false,
        message: 'This meeting has already occurred and cannot be modified.',
        canReschedule: false,
        canCancel: false,
      };
    }

    // For now, allow all future meetings to be rescheduled/canceled
    // This can be extended with meeting-type-specific rules
    return {
      isValid: true,
      canReschedule: true,
      canCancel: true,
    };
  }

  /**
   * Parse token to extract basic info (for validation)
   * Note: This is basic client-side validation. Server does full validation.
   */
  static parseToken(token: string): { bookingId?: string; email?: string; timestamp?: string } | null {
    try {
      const decoded = atob(token); // Use browser-compatible base64 decoding
      const parts = decoded.split(':');
      
      if (parts.length === 3) {
        return {
          bookingId: parts[0],
          email: parts[1],
          timestamp: parts[2],
        };
      }
      
      return null;
    } catch (error) {
      return null;
    }
  }
}

export default BookingActionService;